import { useStore } from '@tanstack/react-form'
import { LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { HTMLAttributes, useState } from 'react'
import Card from 'src/components/containers/card'
import { FlowContext } from 'src/components/flows/types'
import { StepId } from 'src/pages/uni-create/config'

import Alert from '../messages/alert'
import ConfirmationModal from '../modals/confirmation-modal'

export type SourceButtonProps = HTMLAttributes<HTMLDivElement> & {
  imageSrc: string
  label: string
  description: string
  message?: string
  linkText?: string
  connection?: LakehouseConnectionTypeEnum
  context: FlowContext<any>
  to: StepId
  disabled?: boolean
  onClick?: () => void
}

export const SourceButton = ({
  imageSrc,
  label,
  description,
  context,
  connection,
  to,
  disabled,
  message,
  linkText,
  onClick,
  ...rest
}: SourceButtonProps) => {
  const form = context.form
  const currentForm = useStore(form.store, (s) => s)
  const { source } = useStore(form.store, (s) => s.values)
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false)

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (source.connection && source.connection !== connection) {
      setShowSourceModal(true)
      return
    }
    if (connection) {
      form.setFieldValue('source.connection', connection)
    }
    setTimeout(() => context.goToStep(to))
  }

  // When the source is changed, we need to reset the form back to its original state
  // Tanstack form.reset() does not reset the fieldMeta, so we need to manually delete the lingering/dirty data
  const resetForm = () => {
    const allFields = Object.keys(currentForm.fieldMeta)
    allFields.forEach((field) => {
      form.deleteField(field)
    })
    form.reset() // Reset form state back to form.options.defaultValues
    context.resetValidationErrors()
    setShowSourceModal(false)
    setTimeout(() => {
      if (connection) {
        form.setFieldValue('source.connection', connection)
      }
      context.goToStep(to)
    }, 0)
  }

  return (
    <div className='flex w-full items-center gap-4 place-self-stretch p-2 md:w-1/2 lg:w-1/3' {...rest}>
      <button onClick={onClick || handleClick} className='h-full w-full' disabled={disabled}>
        <Card className='transitions h-full hover:shadow-lg hover:shadow-slate-300' ribbonMessage={message}>
          <div className='flex flex-col items-center py-4'>
            <div className='relative mb-4 rounded-full border border-slate-200'>
              <img src={imageSrc} alt={label} className='h-24 w-24 rounded-full' />
            </div>
            <h2 className='h3'>{label}</h2>
            <p className='text-light'>{description}</p>
            <p className='text-link mt-1 font-bold'>{linkText}</p>
          </div>
        </Card>
      </button>
      <ConfirmationModal
        isOpen={showSourceModal}
        onClose={() => setShowSourceModal(false)}
        title={`Before you swap to ${label}`}
        onSubmit={() => resetForm()}
        isSubmitting={false}
        actionButtonType={`primary`}
        actionButtonText={`Change source`}
      >
        <div className='grid gap-4'>
          <Alert>
            Note: Changing to {label} will erase any connection information you have entered. Are you sure you want to
            continue?
          </Alert>
        </div>
      </ConfirmationModal>
    </div>
  )
}
